import { useState, useEffect } from 'react'
import iframeDomainService from '../../services/iframeDomainService'
import {
  HiOutlineGlobe,
  HiOutlineSearch,
  HiOutlinePlus,
  HiOutlinePencil,
  HiOutlineTrash,
  HiOutlineCheck,
  HiOutlineX,
  HiOutlineBan,
  HiOutlineClock,
  HiOutlineRefresh,
} from 'react-icons/hi'
import clsx from 'clsx'
import toast from 'react-hot-toast'

const STATUS_CONFIG = {
  pending: { label: 'Chờ duyệt', color: 'bg-yellow-100 text-yellow-800', icon: HiOutlineClock },
  active: { label: 'Đang hoạt động', color: 'bg-green-100 text-green-800', icon: HiOutlineCheck },
  rejected: { label: 'Đã từ chối', color: 'bg-red-100 text-red-800', icon: HiOutlineX },
  suspended: { label: 'Tạm ngưng', color: 'bg-gray-100 text-gray-800', icon: HiOutlineBan },
}

function IframeDomainManagement() {
  const [domains, setDomains] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [rejectReason, setRejectReason] = useState('')
  const [selectedDomain, setSelectedDomain] = useState(null)
  const [editingDomain, setEditingDomain] = useState(null)
  const [formData, setFormData] = useState({
    domain: '',
    name: '',
    description: '',
    status: 'active',
    allow_subdomains: false,
    requested_by_email: '',
    requested_by_name: '',
    request_reason: '',
    expires_at: '',
  })
  const [pagination, setPagination] = useState({ page: 1, total: 0, pages: 1 })

  useEffect(() => {
    loadDomains()
  }, [searchQuery, statusFilter, pagination.page])

  const loadDomains = async () => {
    setLoading(true)
    try {
      const response = await iframeDomainService.getDomains({
        page: pagination.page,
        per_page: 20,
        search: searchQuery || undefined,
        status: statusFilter || undefined,
      })
      if (response.status === 'success') {
        setDomains(response.data)
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          pages: response.pagination.pages,
        }))
      }
    } catch (error) {
      console.error('Failed to load domains:', error)
      toast.error('Không thể tải danh sách domains')
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    try {
      const payload = { ...formData }
      if (!payload.expires_at) delete payload.expires_at

      if (editingDomain) {
        await iframeDomainService.updateDomain(editingDomain.id, payload)
        toast.success('Đã cập nhật domain')
      } else {
        await iframeDomainService.createDomain(payload)
        toast.success('Đã thêm domain mới')
      }
      setShowModal(false)
      resetForm()
      loadDomains()
    } catch (error) {
      console.error('Failed to save domain:', error)
      toast.error(error.response?.data?.message || 'Lỗi khi lưu')
    }
  }

  const handleEdit = (domain) => {
    setEditingDomain(domain)
    setFormData({
      domain: domain.domain,
      name: domain.name || '',
      description: domain.description || '',
      status: domain.status,
      allow_subdomains: domain.allow_subdomains,
      requested_by_email: domain.requested_by_email || '',
      requested_by_name: domain.requested_by_name || '',
      request_reason: domain.request_reason || '',
      expires_at: domain.expires_at ? domain.expires_at.split('T')[0] : '',
    })
    setShowModal(true)
  }

  const handleDelete = async (domain) => {
    if (!confirm(`Xóa domain "${domain.domain}"?`)) return

    try {
      await iframeDomainService.deleteDomain(domain.id)
      toast.success('Đã xóa domain')
      loadDomains()
    } catch (error) {
      console.error('Failed to delete domain:', error)
      toast.error('Không thể xóa domain')
    }
  }

  const handleApprove = async (domain) => {
    try {
      await iframeDomainService.approveDomain(domain.id)
      toast.success(`Đã duyệt domain "${domain.domain}"`)
      loadDomains()
    } catch (error) {
      console.error('Failed to approve domain:', error)
      toast.error('Không thể duyệt domain')
    }
  }

  const handleReject = async () => {
    if (!selectedDomain) return

    try {
      await iframeDomainService.rejectDomain(selectedDomain.id, rejectReason)
      toast.success(`Đã từ chối domain "${selectedDomain.domain}"`)
      setShowRejectModal(false)
      setRejectReason('')
      setSelectedDomain(null)
      loadDomains()
    } catch (error) {
      console.error('Failed to reject domain:', error)
      toast.error('Không thể từ chối domain')
    }
  }

  const handleSuspend = async (domain) => {
    if (!confirm(`Tạm ngưng domain "${domain.domain}"?`)) return

    try {
      await iframeDomainService.suspendDomain(domain.id)
      toast.success(`Đã tạm ngưng domain "${domain.domain}"`)
      loadDomains()
    } catch (error) {
      console.error('Failed to suspend domain:', error)
      toast.error('Không thể tạm ngưng domain')
    }
  }

  const resetForm = () => {
    setEditingDomain(null)
    setFormData({
      domain: '',
      name: '',
      description: '',
      status: 'active',
      allow_subdomains: false,
      requested_by_email: '',
      requested_by_name: '',
      request_reason: '',
      expires_at: '',
    })
  }

  const openRejectModal = (domain) => {
    setSelectedDomain(domain)
    setShowRejectModal(true)
  }

  const pendingCount = domains.filter(d => d.status === 'pending').length

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-primary-100 rounded-xl flex items-center justify-center">
            <HiOutlineGlobe className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quản lý Iframe Domains</h1>
            <p className="text-sm text-gray-500">
              Quản lý domains được phép nhúng bản đồ qua iframe
            </p>
          </div>
        </div>

        <button
          onClick={() => {
            resetForm()
            setShowModal(true)
          }}
          className="btn-primary flex items-center gap-2"
        >
          <HiOutlinePlus className="w-5 h-5" />
          Thêm Domain
        </button>
      </div>

      {/* Pending Alert */}
      {pendingCount > 0 && (
        <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-xl flex items-center gap-3">
          <HiOutlineClock className="w-6 h-6 text-yellow-600" />
          <div>
            <p className="font-medium text-yellow-800">
              Có {pendingCount} domain đang chờ duyệt
            </p>
            <p className="text-sm text-yellow-600">
              Vui lòng xem xét và duyệt hoặc từ chối các yêu cầu mới
            </p>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px]">
            <HiOutlineSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Tìm kiếm domain, tên, email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input pl-10 w-full"
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input w-48"
          >
            <option value="">Tất cả trạng thái</option>
            <option value="pending">Chờ duyệt</option>
            <option value="active">Đang hoạt động</option>
            <option value="rejected">Đã từ chối</option>
            <option value="suspended">Tạm ngưng</option>
          </select>

          <button
            onClick={loadDomains}
            className="btn-secondary flex items-center gap-2"
          >
            <HiOutlineRefresh className="w-5 h-5" />
            Làm mới
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Domain</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Tên</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Người yêu cầu</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Trạng thái</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Subdomains</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-600">Ngày tạo</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-600">Thao tác</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
                      <span>Đang tải...</span>
                    </div>
                  </td>
                </tr>
              ) : domains.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-12 text-center text-gray-500">
                    <HiOutlineGlobe className="w-12 h-12 mx-auto mb-2 opacity-50" />
                    <p>Chưa có domain nào</p>
                  </td>
                </tr>
              ) : (
                domains.map((domain) => {
                  const statusInfo = STATUS_CONFIG[domain.status] || STATUS_CONFIG.pending
                  const StatusIcon = statusInfo.icon
                  return (
                    <tr key={domain.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <HiOutlineGlobe className="w-4 h-4 text-gray-400" />
                          <span className="font-mono text-sm font-medium text-gray-900">
                            {domain.domain}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {domain.name || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <p className="text-gray-900">{domain.requested_by_name || '-'}</p>
                          <p className="text-gray-500 text-xs">{domain.requested_by_email || '-'}</p>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={clsx(
                          'inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full',
                          statusInfo.color
                        )}>
                          <StatusIcon className="w-3 h-3" />
                          {statusInfo.label}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {domain.allow_subdomains ? (
                          <span className="text-green-600 text-sm flex items-center gap-1">
                            <HiOutlineCheck className="w-4 h-4" />
                            Cho phép
                          </span>
                        ) : (
                          <span className="text-gray-400 text-sm flex items-center gap-1">
                            <HiOutlineX className="w-4 h-4" />
                            Không
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-500">
                        {new Date(domain.created_at).toLocaleDateString('vi-VN')}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {/* Approve/Reject buttons for pending */}
                          {domain.status === 'pending' && (
                            <>
                              <button
                                onClick={() => handleApprove(domain)}
                                className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                                title="Duyệt"
                              >
                                <HiOutlineCheck className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => openRejectModal(domain)}
                                className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                                title="Từ chối"
                              >
                                <HiOutlineX className="w-4 h-4" />
                              </button>
                            </>
                          )}

                          {/* Suspend button for active */}
                          {domain.status === 'active' && (
                            <button
                              onClick={() => handleSuspend(domain)}
                              className="p-2 text-orange-600 hover:bg-orange-50 rounded-lg"
                              title="Tạm ngưng"
                            >
                              <HiOutlineBan className="w-4 h-4" />
                            </button>
                          )}

                          {/* Re-activate for suspended/rejected */}
                          {(domain.status === 'suspended' || domain.status === 'rejected') && (
                            <button
                              onClick={() => handleApprove(domain)}
                              className="p-2 text-green-600 hover:bg-green-50 rounded-lg"
                              title="Kích hoạt lại"
                            >
                              <HiOutlineRefresh className="w-4 h-4" />
                            </button>
                          )}

                          <button
                            onClick={() => handleEdit(domain)}
                            className="p-2 text-gray-600 hover:bg-gray-100 rounded-lg"
                            title="Sửa"
                          >
                            <HiOutlinePencil className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(domain)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                            title="Xóa"
                          >
                            <HiOutlineTrash className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between">
            <p className="text-sm text-gray-500">
              Tổng: {pagination.total} domains
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                disabled={pagination.page === 1}
                className="btn-secondary text-sm disabled:opacity-50"
              >
                Trước
              </button>
              <span className="text-sm text-gray-600">
                Trang {pagination.page} / {pagination.pages}
              </span>
              <button
                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="btn-secondary text-sm disabled:opacity-50"
              >
                Sau
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold">
                {editingDomain ? 'Chỉnh sửa Domain' : 'Thêm Domain mới'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Domain <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={formData.domain}
                  onChange={(e) => setFormData({ ...formData, domain: e.target.value })}
                  placeholder="example.com"
                  className="input w-full"
                  required
                  disabled={!!editingDomain}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Không cần http:// hoặc https://
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên hiển thị
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Tên website"
                  className="input w-full"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Mô tả
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Ghi chú về domain này..."
                  className="input w-full"
                  rows={2}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Trạng thái
                  </label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="input w-full"
                  >
                    <option value="pending">Chờ duyệt</option>
                    <option value="active">Đang hoạt động</option>
                    <option value="rejected">Đã từ chối</option>
                    <option value="suspended">Tạm ngưng</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hết hạn
                  </label>
                  <input
                    type="date"
                    value={formData.expires_at}
                    onChange={(e) => setFormData({ ...formData, expires_at: e.target.value })}
                    className="input w-full"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="allow_subdomains"
                  checked={formData.allow_subdomains}
                  onChange={(e) => setFormData({ ...formData, allow_subdomains: e.target.checked })}
                  className="rounded border-gray-300"
                />
                <label htmlFor="allow_subdomains" className="text-sm text-gray-700">
                  Cho phép tất cả subdomains (*.domain.com)
                </label>
              </div>

              <div className="border-t border-gray-200 pt-4">
                <p className="text-sm font-medium text-gray-700 mb-3">Thông tin người yêu cầu</p>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Tên</label>
                    <input
                      type="text"
                      value={formData.requested_by_name}
                      onChange={(e) => setFormData({ ...formData, requested_by_name: e.target.value })}
                      className="input w-full text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Email</label>
                    <input
                      type="email"
                      value={formData.requested_by_email}
                      onChange={(e) => setFormData({ ...formData, requested_by_email: e.target.value })}
                      className="input w-full text-sm"
                    />
                  </div>
                </div>
                <div className="mt-2">
                  <label className="block text-xs text-gray-500 mb-1">Lý do yêu cầu</label>
                  <textarea
                    value={formData.request_reason}
                    onChange={(e) => setFormData({ ...formData, request_reason: e.target.value })}
                    className="input w-full text-sm"
                    rows={2}
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="btn-secondary"
                >
                  Hủy
                </button>
                <button type="submit" className="btn-primary">
                  {editingDomain ? 'Cập nhật' : 'Thêm mới'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && selectedDomain && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold">Từ chối yêu cầu</h2>
              <p className="text-sm text-gray-500 mt-1">
                Domain: <span className="font-mono">{selectedDomain.domain}</span>
              </p>
            </div>

            <div className="p-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lý do từ chối (tùy chọn)
              </label>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                placeholder="Nhập lý do từ chối..."
                className="input w-full"
                rows={3}
              />

              <div className="flex justify-end gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowRejectModal(false)
                    setRejectReason('')
                    setSelectedDomain(null)
                  }}
                  className="btn-secondary"
                >
                  Hủy
                </button>
                <button
                  onClick={handleReject}
                  className="btn-primary bg-red-600 hover:bg-red-700"
                >
                  Xác nhận từ chối
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default IframeDomainManagement
